import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlayCircle, X } from 'lucide-react';
import type { AppView, Project } from '@/types';
import { getLatestWizardSession, WizardStepNum } from '@/lib/wizardSession';

const STEP_SHORT: Record<WizardStepNum, string> = {
  1: 'Hochladen',
  2: 'Einstellen',
  3: 'Prüfen & Einrichten',
  4: 'Branding & Download',
};

interface WizardResumeBubbleProps {
  projects: Project[];
  currentView: AppView;
  currentProjectId: string | null;
  onResume: (projectId: string) => void;
}

/**
 * Schwebe-Bubble über "Hilfe & Kontakt": zeigt den zuletzt nicht abgeschlossenen
 * Assistenten-Durchlauf und setzt ihn per Klick fort. Liest die jüngste Session
 * aus dem Browser-Speicher; blendet sich aus, wenn man ohnehin gerade im Flow
 * (oder im Workspace desselben Projekts, dort gibt es das Inline-Banner) ist.
 */
export const WizardResumeBubble: React.FC<WizardResumeBubbleProps> = ({
  projects,
  currentView,
  currentProjectId,
  onResume,
}) => {
  // Signatur der weggeklickten Session (projectId:updatedAt) — bei erneuter
  // Änderung im Flow ändert sich updatedAt und die Bubble erscheint wieder.
  const [dismissedSig, setDismissedSig] = useState<string | null>(null);

  const latest = getLatestWizardSession();
  if (!latest) return null;
  if (currentView === 'wizard') return null;
  if (currentView === 'workspace' && currentProjectId === latest.projectId) return null;

  const project = projects.find((p) => p.id === latest.projectId);
  if (!project) return null;

  const sig = `${latest.projectId}:${latest.updatedAt}`;
  if (dismissedSig === sig) return null;

  return (
    <div className="fixed bottom-24 right-6 z-40 flex justify-end">
      <AnimatePresence>
        <motion.div
          key={sig}
          initial={{ opacity: 0, y: 8, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.97 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          className="flex max-w-[280px] items-center gap-2.5 rounded-2xl border border-primary/30 bg-card px-3 py-2.5 shadow-xl"
        >
          <button
            type="button"
            onClick={() => onResume(latest.projectId)}
            className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
            title="Staging fortsetzen"
          >
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
              <PlayCircle className="h-5 w-5 text-primary" />
            </span>
            <span className="min-w-0">
              <span className="block text-xs font-semibold text-foreground">Staging fortsetzen</span>
              <span className="block truncate text-[11px] text-muted-foreground">
                {project.name} · {STEP_SHORT[latest.step]}
              </span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => setDismissedSig(sig)}
            className="flex-shrink-0 text-muted-foreground transition-colors hover:text-foreground"
            aria-label="Ausblenden"
          >
            <X className="h-4 w-4" />
          </button>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
